import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useClient } from '../hooks/useClients';
import Navigation from '../components/Navigation';
import IntegrationCodeDisplay from '../components/IntegrationCodeDisplay';
import MealEntryCard from '../components/MealEntryCard';
import DailySummaryCard from '../components/DailySummaryCard';
import { ArrowLeft, Utensils, ChevronLeft, ChevronRight, Calendar, Link as LinkIcon, Unlink } from 'lucide-react';
import { mealMonitorService } from '../services/mealMonitorService';
import type { MealMonitorStatus, DailyMealReport } from '../types/mealMonitor';

const ClientNutritionPage: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const { trainer } = useAuth();

  const clientIdNumber = parseInt(clientId || '0');

  const { client, isLoading: isClientLoading } = useClient(clientIdNumber);

  const [status, setStatus] = useState<MealMonitorStatus | null>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(true);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [mealReport, setMealReport] = useState<DailyMealReport | null>(null);
  const [isLoadingMeals, setIsLoadingMeals] = useState(false);

  // Загрузка статуса подключения
  const loadStatus = async () => {
    setIsLoadingStatus(true);
    try {
      const statusData = await mealMonitorService.checkStatus(clientIdNumber);
      setStatus(statusData);
    } catch (error) {
      console.error('Error loading status:', error);
    } finally {
      setIsLoadingStatus(false);
    }
  };

  // Генерация кода
  const handleGenerateCode = async () => {
    setIsGeneratingCode(true);
    try {
      const codeResponse = await mealMonitorService.generateCode(clientIdNumber);
      setStatus({
        is_connected: false,
        code: codeResponse.code,
        code_expires_at: codeResponse.expires_at
      });
    } catch (error) {
      console.error('Error generating code:', error);
      alert('Ошибка при генерации кода');
    } finally {
      setIsGeneratingCode(false);
    }
  };

  // Загрузка записей о питании
  const loadMealEntries = async (date: Date) => {
    if (!status?.is_connected) return;

    setIsLoadingMeals(true);
    try {
      const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD
      const report = await mealMonitorService.getEntries(clientIdNumber, dateStr);
      setMealReport(report);
    } catch (error) {
      console.error('Error loading meal entries:', error);
      setMealReport(null);
    } finally {
      setIsLoadingMeals(false);
    }
  };

  // Отключение интеграции
  const handleDisconnect = async () => {
    if (!window.confirm('Вы уверены, что хотите отключить интеграцию с дневником питания?')) {
      return;
    }

    try {
      await mealMonitorService.disconnect(clientIdNumber);
      setMealReport(null);
      // Перезагружаем статус с сервера для актуальных данных
      await loadStatus();
    } catch (error) {
      console.error('Error disconnecting:', error);
      alert('Ошибка при отключении интеграции');
    }
  };

  // Навигация по датам
  const goToPreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const isToday = () => {
    const today = new Date();
    return selectedDate.toDateString() === today.toDateString();
  };

  // Форматирование даты для отображения
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Загрузка статуса при монтировании
  useEffect(() => {
    loadStatus();
  }, [clientIdNumber]);

  // Автоматическая проверка подключения каждые 10 секунд, если есть код но нет подключения
  useEffect(() => {
    if (status && !status.is_connected && status.code) {
      const interval = setInterval(() => {
        loadStatus();
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [status]);

  // Загрузка записей при изменении даты или подключении
  useEffect(() => {
    if (status?.is_connected) {
      loadMealEntries(selectedDate);
    }
  }, [selectedDate, status?.is_connected]);

  if (isClientLoading || isLoadingStatus) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Клиент не найден</h1>
            <button
              onClick={() => navigate('/clients')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Вернуться к клиентам
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Заголовок страницы */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/clients')}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Назад
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  Дневник питания {client.first_name} {client.last_name}
                </h1>
              </div>
            </div>
          </div>
        </div>

        {/* Статус подключения */}
        {status && !status.is_connected && !status.code && (
          <div className="bg-white rounded-lg shadow p-8 text-center mb-6">
            <div className="text-gray-400 mb-4">
              <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                <Utensils className="h-8 w-8" />
              </div>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Дневник питания не подключен
            </h3>
            <p className="text-gray-500 mb-6 max-w-2xl mx-auto">
              Для просмотра дневника питания клиента необходимо подключить интеграцию с Telegram ботом Meal Monitor.
              <br />
              Клиент должен вести дневник в боте: <a href="https://t.me/Meal_Monitor_Bot" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">@Meal_Monitor_Bot</a>
            </p>
            <button
              onClick={handleGenerateCode}
              disabled={isGeneratingCode}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <LinkIcon className="mr-2 h-5 w-5" />
              Получить код подключения
            </button>
          </div>
        )}

        {/* Отображение кода */}
        {status && !status.is_connected && status.code && status.code_expires_at && (
          <div className="mb-6">
            <IntegrationCodeDisplay
              code={status.code}
              expiresAt={status.code_expires_at}
              onGenerateNew={handleGenerateCode}
              isGenerating={isGeneratingCode}
            />
            <div className="mt-4 text-center text-sm text-gray-600">
              Ожидание подключения клиента...
            </div>
          </div>
        )}

        {/* Подключено - показываем дневник */}
        {status?.is_connected && (
          <div className="space-y-6">
            {/* Навигация по датам */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <button
                  onClick={goToPreviousDay}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-gray-400" />
                    <span className="text-lg font-medium text-gray-900">
                      {formatDate(selectedDate)}
                    </span>
                  </div>
                  {!isToday() && (
                    <button
                      onClick={goToToday}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      Сегодня
                    </button>
                  )}
                </div>

                <button
                  onClick={goToNextDay}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Загрузка */}
            {isLoadingMeals && (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            )}

            {/* Записи о питании */}
            {!isLoadingMeals && mealReport && (
              <>
                {Object.keys(mealReport.meals || {}).length > 0 ? (
                  <>
                    <DailySummaryCard
                      totalCalories={mealReport.total_calories}
                      totalProtein={mealReport.total_protein}
                      totalFat={mealReport.total_fat}
                      totalCarbs={mealReport.total_carbs}
                      totalFiber={mealReport.total_fiber}
                      entriesCount={Object.values(mealReport.meals).reduce((acc, meal) => acc + meal.entries.length, 0)}
                      nutritionTargets={mealReport.nutrition_targets}
                    />

                    <div className="space-y-4">
                      {(() => {
                        // Находим самый калорийный продукт за день
                        let highestCalorieEntry: number | null = null;
                        let maxCalories = 0;

                        Object.values(mealReport.meals).forEach((mealGroup) => {
                          mealGroup.entries.forEach((entry) => {
                            const calories = entry.calories || 0;
                            if (calories > maxCalories) {
                              maxCalories = calories;
                              highestCalorieEntry = entry.id;
                            }
                          });
                        });

                        return Object.entries(mealReport.meals).map(([mealKey, mealGroup]) => (
                          <div key={mealKey} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                            {/* Заголовок приема пищи */}
                            <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
                              <h3 className="text-sm font-semibold text-gray-900">
                                {mealGroup.name}
                              </h3>
                              <span className="text-xs text-gray-500">
                                {mealGroup.total_calories.toFixed(0)} ккал
                              </span>
                            </div>

                            {/* Записи приема пищи */}
                            <div className="space-y-2">
                              {mealGroup.entries.map((entry) => (
                                <MealEntryCard
                                  key={entry.id}
                                  entry={entry}
                                  isHighestCalorie={entry.id === highestCalorieEntry}
                                />
                              ))}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  </>
                ) : (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                    <div className="text-gray-400 mb-4">
                      <Utensils className="h-12 w-12 mx-auto" />
                    </div>
                    <p className="text-gray-600">
                      Записей о питании за этот день нет
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Кнопка отключения */}
            <div className="flex justify-center pt-4">
              <button
                onClick={handleDisconnect}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-red-700 hover:text-red-800"
              >
                <Unlink className="mr-2 h-4 w-4" />
                Отключить интеграцию
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientNutritionPage;
